import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { usePmt3ProblemsDashboardData } from '@/hooks/usePmt3ProblemsDashboardData';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { DocumentData } from 'firebase/firestore';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

// --- Formatting Helpers ---
const parseSafeFloat = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleanedValue);
    return isNaN(num) ? null : num;
};

const formatCurrency = (value: any, decimals = 0): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

// Correctly formats a decimal (e.g., 0.25) into a rounded percentage string (e.g., "25%")
const formatDecimalAsPercent = (value: any): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `${Math.round(num * 100)}%`;
};

// Formats a number directly as a percentage (e.g., 25 becomes "25%")
const formatDirectPercent = (value: any): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `${Math.round(num)}%`;
}

const formatNumber = (value: any): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return num.toLocaleString('en-US');
};

const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
};
// Format a value by type (used for old values from the ledger)
const formatValue = (value: any, formatType: FormatType): string => {
    switch (formatType) {
        case 'currency': return formatCurrency(value, 0);
        case 'currency2dec': return formatCurrency(value, 2);
        case 'percent': return formatDecimalAsPercent(value);
        case 'percent_direct': return formatDirectPercent(value);
        case 'number': return formatNumber(value);
        default: return formatDefault(value);
    }
};
// --- End Formatting Helpers ---


// Dashboard Item Configuration
type FormatType = 'currency' | 'percent' | 'number' | 'default' | 'currency2dec' | 'percent_direct';
interface DashboardItemConfig {
    label: string;
    field: string | null; // Firestore field name (use exact case)
    format: FormatType;
}

// Use exact Firestore field names and CORRECTED format types
const allDashboardItems: DashboardItemConfig[] = [
    { label: '28 Day Traffic', field: '28 Day Traffic', format: 'number' },
    { label: '28 Day Conversion Rate', field: '28 Day Conversion Rate', format: 'percent' },
    { label: '28 Day Units', field: '28 Day Units', format: 'number' },
    { label: '28 Day Revenue', field: '28 Day Revenue', format: 'currency' },
    { label: '28 Day PPC Spend', field: '28 Day PPC Spend', format: 'currency' },
    { label: '28 Day Gross Profit', field: '28 Day Gross Profit', format: 'currency' },
    { label: '28 Day Gross Margin', field: '28 Day Gross Margin', format: 'percent' },
    { label: '28 Day TACOS', field: '28 Day TACOS', format: 'percent' },
    { label: '28 Day ACoS', field: '28 Day PPC ACoS', format: 'percent' },
    { label: 'Organic Sales %', field: null, format: 'percent' },
];

// Split items for two columns
const midPoint = Math.ceil(allDashboardItems.length / 2);
const column1ItemsConfig = allDashboardItems.slice(0, midPoint);
const column2ItemsConfig = allDashboardItems.slice(midPoint);

// Simple Item Renderer - Accepts colorClass, bgClass, icon, and oldValue
interface ItemDisplayProps {
    label: string;
    value: string;
    isLoading: boolean;
    colorClass?: string;
    bgClass?: string;
    icon?: React.ReactNode;
    oldValue?: string;
}
const ItemDisplay: React.FC<ItemDisplayProps> = ({ label, value, isLoading, colorClass = '', bgClass = '', icon = null, oldValue }) => (
     <TableRow className={bgClass}>
        <TableCell className="font-medium py-2 px-4">{label}</TableCell>
        <TableCell className={`text-right py-2 px-4 ${colorClass}`}>
            {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                 <div className="flex items-center justify-end space-x-1">
                    {icon}
                    <span className="font-semibold">{value}</span>
                </div>
            )}
        </TableCell>
        <TableCell className="text-right py-2 px-2">
            {isLoading ? <Skeleton className="h-4 w-12 float-right" /> : (
                oldValue && oldValue !== '-' && oldValue !== value ? (
                    <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100 text-xs">
                        {oldValue}
                    </span>
                ) : null
            )}
        </TableCell>
    </TableRow>
);


type OldValuesMap = Record<string, string>;

const OverallResultsDashboard: React.FC = () => {
    const { selectedProduct } = usePMT3Selection();
    const { data, isLoading, error } = usePmt3ProblemsDashboardData(selectedProduct?.companyName);
    const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);

    const currentProductData = data?.productData?.find(p => p.id === selectedProduct?.id);

    // Fetch old values for comparison
    useEffect(() => {
        setOldValuesMap(null);
        if (selectedProduct?.companyName && selectedProduct?.id) {
            fetchLatestOldValuesMap(selectedProduct.companyName, selectedProduct.id)
                .then(setOldValuesMap)
                .catch(() => setOldValuesMap(null));
        }
    }, [selectedProduct]);

    // Helper to get raw and formatted value for an item config
    const getValueAndFormat = (item: DashboardItemConfig, productData: DocumentData | undefined): { formattedValue: string, rawValue: number | null } => {
        let value: any;
        let rawValue: number | null = null;

        if (item.label === 'Organic Sales %') {
            // ** Implemented Correct Formula **
            const revenue = parseSafeFloat(productData?.['28 Day Revenue']);
            const ppcSales = parseSafeFloat(productData?.['28 Day PPC Sales']);
            if (revenue !== null && ppcSales !== null) {
                 // Calculate Total Sales = Revenue + PPC Sales
                 const totalSales = revenue + ppcSales;
                 // Calculate Organic Sales % = Revenue / Total Sales
                 // Handle division by zero
                 rawValue = totalSales !== 0 ? revenue / totalSales : 0;
                 value = rawValue; // Use the calculated decimal for formatting
            } else {
                 value = null; // Cannot calculate if inputs are missing
                 rawValue = null;
            }
        } else if (item.field) {
            value = productData?.[item.field];
             if (['28 Day Gross Profit', '28 Day TACOS', '28 Day ACoS'].includes(item.label)) {
                 rawValue = parseSafeFloat(value);
             }
        } else {
            value = null;
        }

        // Apply formatting based on the item's config
        let formattedValue = '-';
        switch (item.format) {
            case 'currency': formattedValue = formatCurrency(value, 0); break;
            case 'currency2dec': formattedValue = formatCurrency(value, 2); break;
            case 'percent': formattedValue = formatDecimalAsPercent(value); break; // Correctly used now
            case 'percent_direct': formattedValue = formatDirectPercent(value); break;
            case 'number': formattedValue = formatNumber(value); break;
            default: formattedValue = formatDefault(value); break;
        }
         // Ensure rawValue is set if not calculated above but needed for styling
         if (['28 Day Gross Profit', '28 Day TACOS', '28 Day ACoS'].includes(item.label) && rawValue === null) {
             rawValue = parseSafeFloat(value);
         }

        return { formattedValue, rawValue };
    };

     // Helper to determine styling based on item label and raw value
    const getItemStyling = (label: string, rawValue: number | null): { colorClass: string, bgClass: string, icon: React.ReactNode } => {
        let colorClass = 'text-gray-900 dark:text-gray-100'; // Default color
        let bgClass = '';
        let icon: React.ReactNode = null;

        switch (label) {
            case '28 Day Gross Profit':
                if (rawValue !== null) {
                    if (rawValue > 0) {
                        colorClass = 'text-green-700 dark:text-green-500';
                        bgClass = 'bg-green-50 dark:bg-green-900/20';
                        icon = <CheckCircle size={16} className={colorClass} />;
                    } else if (rawValue < 0) {
                        colorClass = 'text-red-600 dark:text-red-500';
                        bgClass = 'bg-red-50 dark:bg-red-900/20';
                        icon = <AlertCircle size={16} className={colorClass} />;
                    } else {
                        colorClass = 'text-yellow-500 dark:text-yellow-400';
                    }
                }
                break;
            case '28 Day TACOS':
                // TACOS > 30% is concerning, stored as decimal (0.30)
                if (rawValue !== null) {
                    if (rawValue > 0.30) {
                        colorClass = 'text-red-600 dark:text-red-500';
                        bgClass = 'bg-red-50 dark:bg-red-900/20';
                        icon = <AlertCircle size={16} className={colorClass} />;
                    } else if (rawValue <= 0.15) {
                        colorClass = 'text-green-700 dark:text-green-500';
                        icon = <CheckCircle size={16} className={colorClass} />;
                    }
                }
                break;
            case '28 Day ACoS':
                // High ACoS (> 40%) is concerning, stored as decimal
                if (rawValue !== null) {
                    if (rawValue > 0.40) {
                        colorClass = 'text-red-600 dark:text-red-500';
                        bgClass = 'bg-red-50 dark:bg-red-900/20';
                        icon = <AlertCircle size={16} className={colorClass} />;
                    } else if (rawValue <= 0.20) {
                        colorClass = 'text-green-700 dark:text-green-500';
                        icon = <CheckCircle size={16} className={colorClass} />;
                    }
                }
                break;
            default:
                break;
        }
        return { colorClass, bgClass, icon };
    };


    if (error) {
        return <Card><CardHeader><CardTitle className="text-2xl font-bold">Overall Results</CardTitle></CardHeader><CardContent><p className="text-red-600 text-sm">Error loading data: {error.message}</p></CardContent></Card>;
    }

     if (!selectedProduct && !isLoading) {
         return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Overall Results</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Overall Results.</p></CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="p-4">
                <CardTitle className="text-2xl font-bold">Overall Results</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                     {/* Column 1 */}
                     <Table>
                         <TableBody>
                             {column1ItemsConfig.map((item) => {
                                const { formattedValue, rawValue } = getValueAndFormat(item, currentProductData);
                                const { colorClass, bgClass, icon } = getItemStyling(item.label, rawValue);
                                const oldValue = item.field ? oldValuesMap?.[item.field] : undefined;
                                const formattedOld = oldValue !== undefined ? formatValue(oldValue, item.format) : undefined;
                                return (
                                    <ItemDisplay
                                        key={item.label}
                                        label={item.label}
                                        value={formattedValue}
                                        isLoading={isLoading}
                                        colorClass={colorClass}
                                        bgClass={bgClass}
                                        icon={icon}
                                        oldValue={formattedOld}
                                    />
                                );
                             })}
                         </TableBody>
                     </Table>
                     {/* Column 2 */}
                     <Table>
                         <TableBody>
                             {column2ItemsConfig.map((item) => {
                                const { formattedValue, rawValue } = getValueAndFormat(item, currentProductData);
                                const { colorClass, bgClass, icon } = getItemStyling(item.label, rawValue);
                                const oldValue = item.field ? oldValuesMap?.[item.field] : undefined;
                                const formattedOld = oldValue !== undefined ? formatValue(oldValue, item.format) : undefined;
                                return (
                                    <ItemDisplay
                                        key={item.label}
                                        label={item.label}
                                        value={formattedValue}
                                        isLoading={isLoading}
                                        colorClass={colorClass}
                                        bgClass={bgClass}
                                        icon={icon}
                                        oldValue={formattedOld}
                                    />
                                );
                             })}
                         </TableBody>
                     </Table>
                 </div>
                 {!currentProductData && !isLoading && (
                     <div className="text-center text-sm text-gray-500 py-4">
                         No data found for selected product.
                     </div>
                 )}
            </CardContent>
        </Card>
    );
};

export default OverallResultsDashboard;